import React, { useState, useEffect, useRef } from 'react';
import { Code2, Database, Terminal, Cpu, GitBranch, Layers } from 'lucide-react';
import { skills } from '../data/mock';
import { Card, CardContent } from './ui/card';

const CircularProgress = ({ skill, delay, color }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setProgress(skill.level);
      }, delay);
    }
  }, [isVisible, skill.level, delay]);

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div ref={ref} className="flex flex-col items-center group">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-[#1f1f23]"
          />
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
            {progress}%
          </span>
        </div>
      </div>
      <span className="mt-3 text-white font-medium text-center">{skill.name}</span>
    </div>
  );
};

const TechCard = ({ icon: Icon, name, color, description }) => {
  return (
    <Card className="bg-[#121214] border-[#1f1f23] hover:border-[#10b981] transition-all duration-300 hover:scale-105 group cursor-pointer">
      <CardContent className="p-6 text-center">
        <div className="mb-4 flex justify-center">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center bg-[#0a0a0b] group-hover:scale-110 transition-transform duration-300"
            style={{ color: color }}
          >
            <Icon size={32} />
          </div>
        </div>
        <h4 className="text-white font-bold mb-2">{name}</h4>
        <p className="text-[#a3a3a3] text-sm">{description}</p>
      </CardContent>
    </Card>
  );
};

const Skills = () => {
  const techCategories = [
    {
      icon: Code2,
      name: 'Frontend Dev',
      color: '#10b981',
      description: 'React, HTML, CSS, JavaScript'
    },
    {
      icon: Database,
      name: 'Backend Dev',
      color: '#06b6d4',
      description: 'Python, REST API, SQL'
    },
    {
      icon: Terminal,
      name: 'Programming',
      color: '#f97316',
      description: 'C++, Python, JavaScript'
    },
    {
      icon: Cpu,
      name: 'Data Structures',
      color: '#10b981',
      description: 'DSA, Algorithms, OOP'
    },
    {
      icon: GitBranch,
      name: 'Version Control',
      color: '#06b6d4',
      description: 'Git, GitHub, Collaboration'
    },
    {
      icon: Layers,
      name: 'Databases',
      color: '#f97316',
      description: 'MongoDB, MySQL, NoSQL'
    }
  ];

  const allSkills = [
    ...skills.frontend,
    ...skills.backend,
    ...skills.other
  ];

  return (
    <section id="skills" className="py-20 bg-[#0a0a0b] relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#10b981] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#06b6d4] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Skills & <span className="text-[#10b981]">Expertise</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#10b981] to-[#06b6d4] mx-auto mb-4"></div>
            <p className="text-[#a3a3a3] max-w-2xl mx-auto">
              A visual representation of my technical proficiency across various technologies and tools
            </p>
          </div>

          {/* Tech Categories Grid */}
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16">
            {techCategories.map((tech, index) => (
              <TechCard key={index} {...tech} />
            ))}
          </div>

          {/* Technology Stack with Icons */}
          <Card className="bg-[#121214] border-[#1f1f23] overflow-hidden">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                My <span className="text-[#06b6d4]">Technology</span> Stack
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {[
                  { name: 'Python', color: '#10b981' },
                  { name: 'JavaScript', color: '#06b6d4' },
                  { name: 'React', color: '#f97316' },
                  { name: 'C++', color: '#10b981' },
                  { name: 'HTML5', color: '#06b6d4' },
                  { name: 'CSS3', color: '#f97316' },
                  { name: 'SQL', color: '#10b981' },
                  { name: 'MongoDB', color: '#06b6d4' },
                  { name: 'REST API', color: '#f97316' },
                  { name: 'Git', color: '#10b981' },
                  { name: 'GitHub', color: '#06b6d4' },
                  { name: 'DSA', color: '#f97316' },
                  { name: 'OOP', color: '#10b981' },
                  { name: 'MySQL', color: '#06b6d4' },
                  { name: 'GUI Dev', color: '#f97316' }
                ].map((tech) => (
                  <div
                    key={tech.name}
                    className="relative group"
                  >
                    <div className="bg-[#0a0a0b] border border-[#1f1f23] rounded-lg p-4 text-center hover:border-[#10b981] transition-all duration-300 hover:scale-105 cursor-default">
                      <div 
                        className="w-2 h-2 rounded-full mx-auto mb-2"
                        style={{ backgroundColor: tech.color }}
                      ></div>
                      <span className="text-[#a3a3a3] text-sm group-hover:text-white transition-colors duration-300">
                        {tech.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Stats Section */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 bg-[#121214] border border-[#1f1f23] rounded-lg hover:border-[#10b981] transition-all duration-300">
              <div className="text-4xl font-bold text-[#10b981] mb-2">15+</div>
              <div className="text-[#a3a3a3]">Technologies Mastered</div>
            </div>
            <div className="text-center p-6 bg-[#121214] border border-[#1f1f23] rounded-lg hover:border-[#06b6d4] transition-all duration-300">
              <div className="text-4xl font-bold text-[#06b6d4] mb-2">4+</div>
              <div className="text-[#a3a3a3]">Projects Completed</div>
            </div>
            <div className="text-center p-6 bg-[#121214] border border-[#1f1f23] rounded-lg hover:border-[#f97316] transition-all duration-300">
              <div className="text-4xl font-bold text-[#f97316] mb-2">2</div>
              <div className="text-[#a3a3a3]">Internship Experiences</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
